// src/features/project/TimelinePanel.tsx
// Gantt-style delivery timeline from GetTimeline. Bars are positioned proportionally
// across the min→max date span; no chart library.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProjectTimeline } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import { formatDate } from './logic';
import type { Entity } from '@core/types/entity';
import type { TimelineItem } from '@core/types/project';

export const TimelinePanel = observer(({ projectEntity }: { projectEntity: Entity }) => {
  const { t, locale } = useI18n();
  const store = useProjectTimeline();
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    store
      .GetTimeline(projectEntity.id)
      .then((res) => {
        if (alive) setItems(res.items?.items ?? []);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [projectEntity.id]);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-8 animate-pulse rounded bg-muted/50" />
        ))}
      </div>
    );
  }

  if (store.error) {
    return <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />;
  }

  if (items.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-lg font-medium text-foreground">{t('project.timeline.empty')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('project.timeline.emptyDesc')}</p>
      </div>
    );
  }

  const starts = items.map((i) => new Date(i.start_date ?? i.end_date ?? Date.now()).getTime());
  const ends = items.map((i) => new Date(i.end_date ?? i.start_date ?? Date.now()).getTime());
  const min = Math.min(...starts);
  const max = Math.max(...ends);
  const span = Math.max(1, max - min);

  return (
    <div className="space-y-3">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{formatDate(new Date(min).toISOString(), locale)}</span>
        <span>{formatDate(new Date(max).toISOString(), locale)}</span>
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => {
          const s = new Date(item.start_date ?? item.end_date ?? min).getTime();
          const e = new Date(item.end_date ?? item.start_date ?? max).getTime();
          const left = ((s - min) / span) * 100;
          const width = Math.max(2, ((e - s) / span) * 100);
          return (
            <div key={idx} className="flex items-center gap-3">
              <span className="w-40 flex-shrink-0 truncate text-sm text-foreground" title={item.title}>
                {item.title}
              </span>
              <div className="relative h-6 flex-1 rounded bg-muted/30">
                <div
                  className={`absolute top-0 flex h-6 items-center rounded px-2 ${item.color ? '' : 'bg-primary/20'}`}
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    backgroundColor: item.color || undefined,
                  }}
                >
                  <div
                    className="h-1.5 rounded-full bg-primary"
                    style={{ width: `${Math.max(0, Math.min(100, item.progress_percent ?? 0))}%` }}
                    aria-hidden="true"
                  />
                </div>
              </div>
              <span className="w-10 flex-shrink-0 text-right text-xs text-muted-foreground">
                {Math.round(item.progress_percent ?? 0)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

TimelinePanel.displayName = 'TimelinePanel';
